import React from 'react';
import { Container, Header, SpaceBetween, Box, Alert, Link, StatusIndicator, Badge } from '@cloudscape-design/components';

/**
 * Live Pricing Check — surfaces the AgentCore Browser repricing result the
 * backend persists on `pricing_calculator.live_check` (P411867476 /
 * P449272857).
 *
 * The backend opens the partner's saved estimate in a live browser, detects
 * the "Update estimate" stale-pricing banner, clicks it, and records the
 * saved-vs-current monthly totals, any services that could not be repriced,
 * and a freshly shared current-pricing link. When a refreshed link is minted
 * it also RE-RUNS the validation Lambda on it, so the decision reflects
 * current AWS prices (validated_on_repriced=true). This card makes that
 * visible on every run where a reprice happened.
 *
 * live_check shape (all optional; None/absent → render nothing):
 *   {
 *     status: 'current' | 'repriced' | 'needs_reconfiguration' | 'unavailable',
 *     saved_monthly: number | null,        // total at the partner's save-time prices
 *     repriced_monthly: number | null,     // total at today's prices
 *     stale_services: string[],            // services that could not auto-reprice
 *     last_updated_text: string,           // "3 months ago"
 *     new_estimate_url: string | null,     // refreshed current-pricing estimate
 *     validated_on_repriced: boolean,      // decision re-run on current prices
 *   }
 *
 * withholdVerdict (the blind page): the card shows only neutral observations: the
 * saved and current amounts, whether every listed service was repriced, and the
 * names of those that were not. No badge, colour, alert or advice.
 *
 * @param {{ pricingData?: { live_check?: object }, withholdVerdict?: boolean }} props
 */
const LivePricingCheck = ({ pricingData, withholdVerdict = false }) => {
  const live = pricingData?.live_check;

  // Render only when a live browser check actually observed a reprice or a
  // reconfiguration need. 'current' (estimate already fresh) and 'unavailable'
  // (check could not run — fail-soft) carry no reviewer-facing signal here.
  if (!live || (live.status !== 'repriced' && live.status !== 'needs_reconfiguration')) {
    return null;
  }

  const savedMonthly = Number(live.saved_monthly) || 0;
  const repricedMonthly = Number(live.repriced_monthly) || 0;
  const savedARR = savedMonthly * 12;
  const repricedARR = repricedMonthly * 12;
  const deltaARR = repricedARR - savedARR;
  const staleServices = Array.isArray(live.stale_services) ? live.stale_services : [];

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);

  const needsReconfig = live.status === 'needs_reconfiguration';
  const changed = Math.abs(deltaARR) >= 0.01;
  const direction = deltaARR > 0 ? 'increased' : 'decreased';

  return (
    <Container
      header={
        <Header
          variant="h3"
          info={!withholdVerdict && live.validated_on_repriced
            ? <Badge color="green">Validated on current prices</Badge>
            : undefined}
        >
          Live Pricing Check
        </Header>
      }
    >
      <SpaceBetween size="s">
        <Box variant="small" color="text-status-inactive">
          The partner&apos;s saved estimate carried a stale-pricing banner. It was
          repriced against current AWS pricing
          {live.last_updated_text ? ` (last saved ${live.last_updated_text})` : ''}.
          {!withholdVerdict && live.validated_on_repriced
            ? ' Validation was run against these current prices.'
            : ''}
        </Box>

        <div>
          <Box variant="awsui-key-label">Saved-time Annual Total</Box>
          <Box fontSize="heading-m" fontWeight="bold">{formatCurrency(savedARR)}</Box>
        </div>

        <div>
          <Box variant="awsui-key-label">Current-pricing Annual Total</Box>
          <Box
            fontSize="heading-m"
            fontWeight="bold"
            color={withholdVerdict ? undefined : (changed ? 'text-status-error' : 'text-status-success')}
          >
            {formatCurrency(repricedARR)}
          </Box>
          <Box variant="small" color="text-status-inactive">
            {changed
              ? `${direction} by ${formatCurrency(Math.abs(deltaARR))}/yr vs saved`
              : 'unchanged — prices have not moved for these services'}
          </Box>
        </div>

        {withholdVerdict ? (
          <SpaceBetween size="xxs">
            <Box variant="p">
              {needsReconfig
                ? 'Some services were not automatically repriced.'
                : 'All listed services were repriced.'}
            </Box>
            {needsReconfig && staleServices.length > 0 && (
              <Box variant="small">{staleServices.slice(0, 5).join('; ')}
                {staleServices.length > 5 ? ` (+${staleServices.length - 5} more)` : ''}
              </Box>
            )}
          </SpaceBetween>
        ) : needsReconfig ? (
          <Alert type="warning" header="Reconfiguration required">
            <SpaceBetween size="xxs">
              <Box variant="p">
                {staleServices.length} service(s) could not be automatically
                repriced (status &quot;Check for updates&quot; / &quot;Required
                inputs&quot;). The partner must update these and resubmit.
              </Box>
              {staleServices.length > 0 && (
                <Box variant="small">{staleServices.slice(0, 5).join('; ')}
                  {staleServices.length > 5 ? ` (+${staleServices.length - 5} more)` : ''}
                </Box>
              )}
            </SpaceBetween>
          </Alert>
        ) : (
          <StatusIndicator type="success">
            All services repriced cleanly
          </StatusIndicator>
        )}

        {live.new_estimate_url && (
          <div>
            <Box variant="awsui-key-label">Current-pricing Estimate</Box>
            <Link href={live.new_estimate_url} external>
              Open refreshed estimate
            </Link>
          </div>
        )}
      </SpaceBetween>
    </Container>
  );
};

export default LivePricingCheck;
